import { Form } from 'antd';
import type { ReactNode } from 'react';

import { CoolToKnow } from 'app/components/commons/CoolToKnow/CoolToKnow';
import { AccessMode } from 'app/components/pages/Experiences/Common/_components/Accessibility/_components/AccessMode';
import { RelatedSpaces } from 'app/components/pages/Experiences/Common/_components/Accessibility/_components/RelatedSpaces';
import { Description } from 'app/components/pages/Experiences/Common/_components/Copywriting/_components/Description';
import { Brand } from 'app/components/pages/Experiences/Common/_components/FormItems/BasicInformations/Brand';
import { Duration } from 'app/components/pages/Experiences/Common/_components/FormItems/BasicInformations/Duration';
import { Location } from 'app/components/pages/Experiences/Common/_components/FormItems/BasicInformations/Location';
import { Time } from 'app/components/pages/Experiences/Common/_components/FormItems/BasicInformations/Time';
import { OpeningHours } from 'app/components/pages/Experiences/Common/_components/FormItems/OpeningHours/OpeningHours';
import { Photos } from 'app/components/pages/Experiences/Common/_components/Medias/_components/Photos';
import { ExperienceFieldType } from 'app/typings/experiences';
import type { ExperienceCategory } from 'app/typings/experiences';

import styles from './BuilderExperienceForm.module.scss';
import { Price } from './_components/Price';
import { RoomService } from './_components/RoomService';

type FormPartProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
};

const FormPart = ({ title, subtitle, children }: FormPartProps) => (
  <div className={styles.formPart}>
    <div className={styles.formPartTitle}>{title}</div>
    {!!subtitle && <div className={styles.formPartSubtitle}>{subtitle}</div>}
    <div className={styles.formPartContent}>{children}</div>
  </div>
);

type Props = {
  category: ExperienceCategory;
};

export const BuilderExperienceForm = ({ category }: Props) => {
  const hasOpeningHours =
    category.openingHours !== ExperienceFieldType.NOT_APPLICABLE;

  const hasPracticalInformation =
    !!category.spaces ||
    !!category.accessMode ||
    hasOpeningHours ||
    !!category.roomService;

  return (
    <div className={styles.builderExperienceForm}>
      <FormPart title="Basic information">
        {!!category.brand && <Brand />}
        {!!category.duration && <Duration />}
        {!!category.time && <Time />}
        {!!category.locations && <Location />}
        <Description />
      </FormPart>

      {hasPracticalInformation && (
        <FormPart
          title="Practical information"
          subtitle={
            hasOpeningHours
              ? 'Add practical information regarding the opening hours of the activity. This information will be shown at the bottom of the Package page.'
              : undefined
          }
        >
          {!!category.spaces && <RelatedSpaces />}
          {!!category.accessMode && <AccessMode />}
          {hasOpeningHours && <OpeningHours />}
          {!!category.roomService && <RoomService />}
        </FormPart>
      )}

      {!!category.price && (
        <FormPart title="Price">
          <Price />
        </FormPart>
      )}

      <FormPart title="Photos">
        <Photos />
      </FormPart>

      <Form.Item name="allowedPaxTypes" hidden>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="categoryId" hidden>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="hotelId" hidden>
        <input type="hidden" />
      </Form.Item>

      <CoolToKnow />
    </div>
  );
};
